import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';
import ChatService from '../service/ChatService';
import './ChatAssistant.css';

const ChatAssistant = () => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatContainerRef = useRef(null);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const getErrorMessage = (error) => {
        if (error.response) {
            const status = error.response.status;
            const errorData = error.response.data;

            switch (status) {
                case 400:
                    return errorData.error || t('chat_assistant_error_invalid_request');
                case 401:
                    return t('chat_assistant_error_unauthorized');
                case 402:
                    return t('chat_assistant_error_quota_exceeded');
                case 500:
                    if (errorData.error === 'OpenAI API key is not configured') {
                        return t('chat_assistant_error_api_key_missing');
                    }
                    return t('chat_assistant_error_server');
                default:
                    return t('chat_assistant_error');
            }
        }
        return t('chat_assistant_error_network');
    };

    const sendMessage = async () => {
        if (!inputMessage.trim()) return;

        const userMessage = {
            text: inputMessage,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        setInputMessage('');

        try {
            const response = await ChatService.sendMessage(inputMessage);
            
            const assistantMessage = {
                text: response.choices[0].message.content,
                sender: 'assistant',
                timestamp: new Date().toLocaleTimeString()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                text: getErrorMessage(error),
                sender: 'system',
                timestamp: new Date().toLocaleTimeString()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="chat-assistant">
            <div className="chat-messages" ref={chatContainerRef}>
                {messages.length === 0 && (
                    <div className="welcome-message">
                        <Message 
                            severity="info" 
                            text={t('chat_assistant_welcome')}
                        />
                    </div>
                )}
                {messages.map((msg, index) => (
                    <div key={index} className={`message ${msg.sender}`}>
                        <div className="message-content">
                            <div className="message-text">{msg.text}</div>
                            <div className="message-timestamp">{msg.timestamp}</div>
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="message assistant">
                        <div className="message-content">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="chat-input">
                <InputText
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('chat_assistant_input_placeholder')}
                    disabled={isLoading}
                />
                <Button
                    icon="pi pi-send"
                    onClick={sendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    className="p-button-rounded"
                    tooltip={t('chat_assistant_send')}
                />
            </div>
        </div>
    );
};

export default ChatAssistant;
